import React, { useEffect, useState } from 'react';
import { Dropdown } from './Dropdown';
import { applyTheme, getThemePref, persistTheme, watchSystemTheme, type ResolvedTheme, type ThemePref } from '../theme';

export interface ThemeMenuLabels {
  /** Accessible name for the trigger. */
  label?: string;
  light?: string;
  dark?: string;
  system?: string;
}

interface ThemeMenuProps {
  labels?: ThemeMenuLabels;
}

const SunIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <circle cx="12" cy="12" r="4" />
    <path strokeLinecap="round" d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

const MoonIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
  </svg>
);

/**
 * Header theme switcher: light, dark, or follow the machine.
 *
 * <p>
 * Sits next to {@link LanguageMenu} and is built on the same {@link Dropdown}, because the two are
 * the same kind of thing — a small, always-available display preference — and a user who has found
 * one should not have to look elsewhere for the other.
 * </p>
 *
 * <p>
 * The trigger shows what the screen is <em>currently</em> doing (sun or moon), not what was picked;
 * on `system` those differ, and the icon answering "why is this dark?" is more useful than one
 * repeating a menu state. The tick inside the menu is what records the choice.
 * </p>
 *
 * <p>
 * Labels are passed in rather than translated here: shared-ui has no i18n instance of its own, and
 * each app already holds one.
 * </p>
 */
export const ThemeMenu: React.FC<ThemeMenuProps> = ({ labels }) => {
  const [pref, setPref] = useState<ThemePref>(() => getThemePref());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => applyTheme(getThemePref()));

  // The boot script in index.html already set the attribute before first paint; this keeps React's
  // idea of it in step, and follows the OS for as long as the preference is `system`.
  useEffect(() => watchSystemTheme(setResolved), []);

  const choose = (next: ThemePref) => {
    setPref(next);
    setResolved(persistTheme(next));
  };

  const options: { value: ThemePref; label: string }[] = [
    { value: 'light', label: labels?.light ?? 'Light' },
    { value: 'dark', label: labels?.dark ?? 'Dark' },
    { value: 'system', label: labels?.system ?? 'System' },
  ];

  return (
    <Dropdown
      aria-label={labels?.label ?? 'Theme'}
      triggerClassName="flex items-center gap-1.5 rounded-md border border-line px-2 py-1.5 text-xs font-medium text-muted-strong hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      panelClassName="w-40"
      trigger={resolved === 'dark' ? <MoonIcon /> : <SunIcon />}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="menuitem"
          onClick={() => choose(o.value)}
          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-canvas ${
            o.value === pref ? 'font-medium text-brand-ink-strong' : 'text-body-soft'
          }`}
        >
          {o.label}
          {o.value === pref && <span className="text-brand-ink">✓</span>}
        </button>
      ))}
    </Dropdown>
  );
};
